import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { Bookmark, BookmarkActionViewMode } from '../../types/api';
import Popover, { AnchorPosition } from '../ui/Popover';
import ButtonIcon from '../ui/ButtonIcon';
import BookmarkActionsMenu, { type BookmarkActionsView } from './BookmarkActionsMenu';

interface BookmarkActionsPopoverProps {
  bookmark: Bookmark;
  viewMode?: BookmarkActionViewMode;
  currentFolderId?: string;
  className?: string;
  /** Controlled open for context menu: when set, popover opens at anchorPosition */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When set, positions popover at this viewport coordinate (e.g. right-click). */
  anchorPosition?: AnchorPosition | null;
  /** Sub-view to open into, e.g. straight to folders for the `m` shortcut. */
  initialView?: BookmarkActionsView;
}

function BookmarkActionsPopover({
  bookmark,
  viewMode = 'home',
  currentFolderId,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  anchorPosition = null,
  initialView = 'menu',
}: BookmarkActionsPopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (isControlled) {
      controlledOnOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };

  return (
    <Popover
      placement="bottom-start"
      open={open}
      onOpenChange={setOpen}
      preventCloseOnContentClick
      anchorPosition={anchorPosition}
      offset={0}
      role="menu"
      content={
        <BookmarkActionsMenu
          bookmark={bookmark}
          viewMode={viewMode}
          currentFolderId={currentFolderId}
          initialView={initialView}
          onClose={() => setOpen(false)}
        />
      }
    >
      <ButtonIcon
        icon="more"
        title={t`Article actions`}
        className="shrink-0"
        iconClassName="rotate-90"
      />
    </Popover>
  );
}

export default BookmarkActionsPopover;
